// Typed HTTP wrapper over the existing axios instance.
//
// Why a wrapper rather than letting features call axios directly:
//   1. One place to declare response/body types — easier to refactor
//      when an endpoint shape changes.
//   2. Normalized errors via ApiError class — features handle one error
//      type, not raw AxiosError unions.
//   3. Future telemetry hooks (timing, retry counts) live here without
//      every feature touching their own axios calls.
//
// The underlying axios instance from api/client.ts handles auth headers,
// 401 token refresh, and the redirect-on-auth-failure flow. Don't
// duplicate that logic here.

import { AxiosError, AxiosRequestConfig } from 'axios';
import { api } from './client';
import type { ApiErrorBody } from './types';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: Record<string, string>;

  constructor(status: number, code: string, message: string, details?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True for 401/403 — caller may want to redirect or show a forbidden page. */
  isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** True for 5xx — caller may show a generic "service unavailable" message. */
  isServerError(): boolean {
    return this.status >= 500;
  }
}

function normalizeError(err: unknown): never {
  if (err instanceof AxiosError) {
    const status = err.response?.status ?? 0;
    const body = err.response?.data as ApiErrorBody | undefined;
    const code = body?.code ?? (status === 0 ? 'NETWORK' : `HTTP_${status}`);
    const message = body?.error ?? err.message ?? 'Request failed';
    throw new ApiError(status, code, message, body?.details);
  }
  if (err instanceof Error) {
    throw new ApiError(0, 'CLIENT', err.message);
  }
  throw new ApiError(0, 'UNKNOWN', 'Unknown error');
}

export const http = {
  async get<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const r = await api.get<T>(path, config);
      return r.data;
    } catch (err) {
      normalizeError(err);
    }
  },
  async post<T, B = unknown>(path: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    try {
      const r = await api.post<T>(path, body, config);
      return r.data;
    } catch (err) {
      normalizeError(err);
    }
  },
  async put<T, B = unknown>(path: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    try {
      const r = await api.put<T>(path, body, config);
      return r.data;
    } catch (err) {
      normalizeError(err);
    }
  },
  async patch<T, B = unknown>(path: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    try {
      const r = await api.patch<T>(path, body, config);
      return r.data;
    } catch (err) {
      normalizeError(err);
    }
  },
  async delete<T = void>(path: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const r = await api.delete<T>(path, config);
      return r.data;
    } catch (err) {
      normalizeError(err);
    }
  },
};
